/**
 * Workspace Home (S08) — the anchor screen all post-login flows return to.
 *
 * A thin dispatcher: it reads the signed-in user's session (`identity.me`)
 * and renders the right simplified home for their tenant vertical + role:
 *
 *   - garment tenant (garment-entitled AND has ≥1 style file, any status)
 *       → garment home
 *   - otherwise (finance, incl. garment-entitled tenants with no style files
 *       yet) → finance home
 *
 * The garment module alone isn't the signal — finance tenants can carry it
 * too (the demo's Loomwell does, with zero styles). We treat a tenant as
 * "garment" once it actually has Style Files (`styles.list` reports
 * `counts.all > 0`). Each home component fetches its own data, so this file
 * stays a thin dispatcher.
 *
 * @see prototype/home/dashboard-simple.html — finance baseline
 * @see prototype/garment/home-simple.html — garment baseline
 * @see docs/spec/09-persona-journeys.md — S08 is the founder anchor
 */
import { createFileRoute } from "@tanstack/react-router";
import { Card, CardBody } from "@flint/ui";
import { trpc } from "../../lib/trpc.ts";
import { WorkspaceShell } from "../../features/workspace/index.ts";
import { FounderHome, OperatorHome } from "../../features/dashboard/index.ts";
import { GarmentOwnerHome, GarmentMerchandiserHome } from "../../features/styles/index.ts";

export const Route = createFileRoute("/(workspace)/")({
  component: WorkspaceHome,
});

function WorkspaceHome() {
  const meQ = trpc.identity.me.useQuery({});
  const company = meQ.data?.company ?? null;
  const garmentEntitled = (company?.modules ?? []).some((m) => m.startsWith("garment"));

  // Only garment-entitled tenants may call styles.list; gate the query so a
  // finance-only tenant never hits the entitled procedure. limit:1 — we only
  // need the `counts.all` rollup, not the rows.
  const stylesQ = trpc.styles.list.useQuery({ limit: 1 }, { enabled: garmentEntitled });

  const gating = meQ.isLoading || (garmentEntitled && stylesQ.isLoading);
  if (gating) {
    return (
      <WorkspaceShell breadcrumbs={[{ label: "Home" }]}>
        <Card>
          <CardBody>
            <p className="text-sm text-muted">Loading your workspace…</p>
          </CardBody>
        </Card>
      </WorkspaceShell>
    );
  }

  // A garment-entitled tenant with at least one Style File (any status) is a
  // garment tenant; one with the module but no styles (Loomwell) falls through
  // to the finance home.
  const isGarment = garmentEntitled && (stylesQ.data?.counts.all ?? 0) > 0;
  // Operators get the action-first variant; Founders/Employees the glance home.
  const isOperator = company?.role === "Operator";

  return (
    <WorkspaceShell breadcrumbs={[{ label: "Home" }]}>
      {isGarment ? (
        isOperator ? (
          <GarmentMerchandiserHome />
        ) : (
          <GarmentOwnerHome />
        )
      ) : isOperator ? (
        <OperatorHome />
      ) : (
        <FounderHome />
      )}
    </WorkspaceShell>
  );
}
